import { formatQuery, RuleType } from "react-querybuilder"
import { useQsFilters } from "../../hooks/filters"
import React, { useMemo, useState } from "react"
import {
  useSearchCounts,
  useFondiSearchList,
  useDocumentiSearchList,
  useSupportiSearchList,
  useSearchFields,
  useDownloadFondiRicerca,
  useDownloadDocumentiRicerca,
  useDownloadSupportiRicerca,
} from "../../hooks/ricerca"
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material"
import { Star, Layers, PlayCircle } from "@mui/icons-material"
import { useMeasure } from "@uidotdev/usehooks"
import { DataGrid, GridRowsProp, GridColDef } from "@mui/x-data-grid"
import { useIsMobile } from "../../hooks/mobile"
import { useTranslation } from "react-i18next"
import LangLink from "../../components/LangLink"
import { Vocabolario, Fondo, Supporto, Documento } from "../../types"
import Paginator from "../../components/Paginator"
import { getValueLabels } from "./fields"
import filesaver from "file-saver"

import { PAGE_SIZE_OPTIONS, usePageSize } from "../../hooks/pageSize"
const initFilters = (params: URLSearchParams) => ({
  query: params.get("query") ?? "",
  fondoPage: Number(params.get("fondoPage") ?? 1),
  documentoPage: Number(params.get("documentoPage") ?? 1),
  supportoPage: Number(params.get("supportoPage") ?? 1),
})

type TabButtonProps = { iconType: React.ElementType; label: string; active: boolean; onClick: () => void }

function TabButton({ iconType, label, active, onClick }: TabButtonProps) {
  const icon =
    iconType &&
    React.createElement(iconType, {
      sx: { mr: 0.5 },
      fontSize: "inherit",
    })
  return (
    <Chip
      icon={icon}
      label={label}
      color="primary"
      onClick={onClick}
      sx={{
        mr: 1,
        textTransform: "capitalize",
        fontWeight: "500",
        cursor: "pointer",
        backgroundColor: active ? "primary.main" : "grey.300",
        color: active ? "white" : "grey.700",
        "&:hover": {
          backgroundColor: active ? "primary.main" : "grey.400",
          color: active ? "white" : "grey.700",
        },
      }}
    />
  )
}

type ResultsListProps<T> = {
  data: { results: T[]; count: number } | undefined
  columns: GridColDef[]
  isLoading?: boolean
  page: number
  setPage: (page: number) => void
}

function ResultsList<T>({ data, columns, isLoading = false, page, setPage }: ResultsListProps<T>) {
  const [ref, { height }] = useMeasure()
  const { t } = useTranslation()
  const pageSize = usePageSize()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  return (
    <Box flexGrow={1}>
      <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div
          style={{
            flexGrow: 1,
            overflow: "scroll",
          }}
          ref={ref}
        >
          {height && (
            <DataGrid
              disableColumnMenu
              disableColumnResize
              disableColumnSorting
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              paginationModel={{ page: 0, pageSize }}
              sx={{
                padding: 2,
                "&.MuiDataGrid-root": {
                  border: "none",
                },
              }}
              // change label when no data
              localeText={{
                noRowsLabel: t("general.nessun_dato_disponibile"),
              }}
              loading={isLoading}
              slots={{
                footer: () => <Paginator numPages={numPages} page={page} setPage={setPage} />,
                toolbar: null,
              }}
              rows={data?.results ?? ([] as GridRowsProp)}
              columns={columns}
            />
          )}
        </div>
      </Paper>
    </Box>
  )
}

export default function RicercaRisultati({}) {
  const pageSize = usePageSize()
  const { filters, setFilters } = useQsFilters(initFilters)
  const previousPageSize = React.useRef(pageSize)

  React.useEffect(() => {
    if (previousPageSize.current === pageSize) return
    previousPageSize.current = pageSize
    setFilters({ fondoPage: 1, documentoPage: 1, supportoPage: 1 })
  }, [pageSize, setFilters])

  const [activeTab, setActiveTab] = useState<"fondo" | "documento" | "supporto">("supporto")

  const serverQuery = useMemo(() => {
    if (!filters.query) return {}
    const query = JSON.parse(filters.query)
    return formatQuery(query, "prisma")
  }, [filters.query])

  const { data: fields } = useSearchFields()

  const natualLangQuery = useMemo(() => {
    if (!filters.query || !fields) return ""
    const query = JSON.parse(filters.query)
    return formatQuery(query, {
      format: "natural_language",
      fields,
      valueProcessor: (rule: RuleType) => getValueLabels(rule, fields),
    })
  }, [filters.query, fields])

  const isEmptyQuery = useMemo(() => {
    if (!filters.query) return true
    const localQuery = JSON.parse(filters.query)
    return !localQuery || localQuery.rules.length === 0
  }, [filters.query])

  const { data: searchCounts } = useSearchCounts({ query: JSON.stringify(serverQuery) })
  const { data: fondiList } = useFondiSearchList(
    { query: JSON.stringify(serverQuery), page: filters.fondoPage, page_size: pageSize },
    activeTab === "fondo"
  )
  const { data: documentiList } = useDocumentiSearchList(
    { query: JSON.stringify(serverQuery), page: filters.documentoPage, page_size: pageSize },
    activeTab === "documento"
  )
  const { data: supportiList } = useSupportiSearchList(
    { query: JSON.stringify(serverQuery), page: filters.supportoPage, page_size: pageSize },
    activeTab === "supporto"
  )

  const { mutateAsync: downloadFondi, isPending: isPendingFondi } = useDownloadFondiRicerca({
    query: JSON.stringify(serverQuery),
    page: filters.fondoPage,
  })

  const { mutateAsync: downloadDocumenti, isPending: isPendingDocumenti } = useDownloadDocumentiRicerca({
    query: JSON.stringify(serverQuery),
    page: filters.documentoPage,
  })

  const { mutateAsync: downloadSupporti, isPending: isPendingSupporti } = useDownloadSupportiRicerca({
    query: JSON.stringify(serverQuery),
    page: filters.supportoPage,
  })

  const isMobile = useIsMobile()

  const { t } = useTranslation()

  const fondiColumns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []

    cols.push({
      field: "segnatura",
      headerName: t("fondo.segnatura"),
      minWidth: 160,
      renderCell: (params) => (
        <LangLink to={`/fondi/${params.row.id}`} style={{ color: "black" }}>
          {params.value}
        </LangLink>
      ),
    })
    if (isMobile) {
      return cols
    }
    cols.push(
      {
        field: "denominazione",
        headerName: t("fondo.denominazione"),
        flex: 1,
      },
      {
        field: "tipologia_data",
        valueGetter: (value: Vocabolario) => value?.termine ?? "",
        headerName: t("fondo.tipologia"),
        flex: 1,
      },
      {
        field: "numero_pellicole",
        headerName: t("fondo.numero_pellicole"),
        flex: 1,
      },
      {
        field: "durata_totale",
        headerName: t("fondo.durata_totale"),
        flex: 1,
      }
    )

    return cols
  }, [t, isMobile])

  const supportiColumns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols.push({
      field: "segnatura",
      headerName: t("supporto.segnatura"),
      minWidth: 160,
      renderCell: (params) => {
        const toLink = `/supporti/${params.row.id}`
        return (
          <LangLink to={toLink} style={{ color: "black" }}>
            {params.row?.segnatura}
          </LangLink>
        )
      },
    })
    if (isMobile) {
      return cols
    }

    cols.push(
      {
        field: "titolo_proprio",
        headerName: t("supporto.titolo_proprio"),
        flex: 1,
      },
      {
        field: "formato_data",
        headerName: t("supporto.formato"),
        flex: 1,
        renderCell: (params) => params.row?.formato_data?.nome,
      },
      {
        field: "cronologia_da",
        headerName: t("fondo.cronologia_da"),
        flex: 1,
        renderCell: (params) => {
          return params.row.fondo_data.cronologia_da
        },
      },
      {
        field: "cronologia_a",
        headerName: t("fondo.cronologia_a"),
        flex: 1,
        renderCell: (params) => {
          return params.row.fondo_data.cronologia_a
        },
      },
      {
        field: "lunghezza",
        headerName: t("supporto.lunghezza"),
        flex: 1,
      },
      {
        field: "durata_presunta",
        headerName: t("supporto.durata_presunta"),
        flex: 1,
      }
    )

    return cols
  }, [t, isMobile])

  const documentiColumns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols.push({
      field: "titolo_proprio",
      headerName: t("documento.titolo_proprio"),
      flex: 1,
      renderCell: (params) => (
        <LangLink to={`/documenti/${params.row.id}`} style={{ color: "black" }}>
          {params.value || t("general.senza_titolo")}
        </LangLink>
      ),
    })
    if (isMobile) {
      return cols
    }
    cols.push(
      {
        field: "titolo_attribuito",
        headerName: t("documento.titolo_attribuito"),
        flex: 1,
      },

      {
        field: "supporti_data",
        headerName: t("general.supporti"),
        flex: 1,
        renderCell: (params) => params.value.map((s: Supporto) => s.segnatura).join(", "),
      },
      {
        field: "genere_data",
        headerName: t("documento.genere"),
        flex: 1,
        renderCell: (params) => {
          return params.row.genere_data?.termine
        },
      }
    )

    return cols
  }, [t, isMobile])

  return (
    <div>
      <Box>
        <Paper sx={{ px: 2, py: 1, mb: 2 }}>
          <Typography variant="caption" sx={{ mb: 0 }}>
            {t("general.query")}
          </Typography>
          {!isEmptyQuery && <Typography>{natualLangQuery}</Typography>}
          {isEmptyQuery && (
            <Typography>
              <i>{t("general.nessuna_query")}</i>
            </Typography>
          )}
        </Paper>
      </Box>
      <Box display={"flex"} justifyContent="space-between" alignItems="center" sx={{ mt: 1, mb: 2 }}>
        <Stack direction="row" overflow={"auto"} spacing={2}>
          <TabButton
            iconType={Layers}
            label={`Supporti (${searchCounts?.supporti})`}
            active={activeTab === "supporto"}
            onClick={() => setActiveTab("supporto")}
          />
          <TabButton
            iconType={PlayCircle}
            label={`Documenti (${searchCounts?.documenti})`}
            active={activeTab === "documento"}
            onClick={() => setActiveTab("documento")}
          />
          <TabButton
            iconType={Star}
            label={`Fondi (${searchCounts?.fondi})`}
            active={activeTab === "fondo"}
            onClick={() => setActiveTab("fondo")}
          />
        </Stack>
        <Button
          variant="contained"
          color="secondary"
          disabled={isPendingFondi || isPendingDocumenti || isPendingSupporti}
          onClick={() => {
            if (activeTab === "fondo") {
              downloadFondi().then((blob) => {
                filesaver.saveAs(blob, `fondi_${new Date().toISOString().split("T")[0]}.xlsx`)
              })
            } else if (activeTab === "documento") {
              downloadDocumenti().then((blob) => {
                filesaver.saveAs(blob, `documenti_${new Date().toISOString().split("T")[0]}.xlsx`)
              })
            } else if (activeTab === "supporto") {
              downloadSupporti().then((blob) => {
                filesaver.saveAs(blob, `supporti_${new Date().toISOString().split("T")[0]}.xlsx`)
              })
            }
          }}
          
        >
          {isPendingFondi || isPendingDocumenti || isPendingSupporti ? (
            <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
          ) : null}
          {t("general.download_excel")}
        </Button>
      </Box>
      {/* <pre>{JSON.stringify(serverQuery)}</pre> */}
      {activeTab === "fondo" && (
        <ResultsList<Fondo>
          data={fondiList}
          columns={fondiColumns}
          isLoading={!searchCounts || !fondiList}
          page={filters.fondoPage}
          setPage={(page: number) => setFilters({ fondoPage: page })}
        />
      )}
      {activeTab === "supporto" && (
        <ResultsList<Supporto>
          data={supportiList}
          columns={supportiColumns}
          isLoading={!searchCounts || !supportiList}
          page={filters.supportoPage}
          setPage={(page: number) => setFilters({ supportoPage: page })}
        />
      )}

      {activeTab === "documento" && (
        <ResultsList<Documento>
          data={documentiList}
          columns={documentiColumns}
          isLoading={!searchCounts || !documentiList}
          page={filters.documentoPage}
          setPage={(page: number) => setFilters({ documentoPage: page })}
        />
      )}
    </div>
  )
}
